import { api, unwrap } from "@lumberset/api-client";
import { queryOptions } from "@tanstack/react-query";
import type { TicketKind, TicketStatus } from "./labels";

/**
 * Everything the support screens read, as query definitions the apps and the
 * shared components share. Keys all start with "support", so one
 * `invalidateQueries({ queryKey: ["support"] })` refreshes every screen.
 */

/** The typed API client. Every app passes its own, which is the same one. */
export type SupportClient = typeof api;

export interface StaffTicketFilters {
  status?: TicketStatus | "active" | "all";
  kind?: TicketKind;
  /** `me`, `unassigned`, or a Staff user id. */
  assignee?: string;
  q?: string;
  page: number;
}

const fetchTickets = (client: SupportClient) => unwrap(client.support.tickets.$get());

const fetchTicket = (client: SupportClient, id: string) =>
  unwrap(client.support.tickets[":id"].$get({ param: { id } }));

const fetchStaffTickets = (client: SupportClient, filters: StaffTicketFilters) =>
  unwrap(
    client.staff.support.tickets.$get({
      query: {
        status: filters.status,
        kind: filters.kind,
        assignee: filters.assignee,
        q: filters.q,
        page: String(filters.page),
      },
    }),
  );

const fetchStaffTicket = (client: SupportClient, id: string) =>
  unwrap(client.staff.support.tickets[":id"].$get({ param: { id } }));

const fetchStats = (client: SupportClient) => unwrap(client.staff.support.stats.$get());

const fetchStaff = (client: SupportClient) => unwrap(client.staff.support.staff.$get());

export type TicketList = Awaited<ReturnType<typeof fetchTickets>>;
export type TicketSummary = TicketList["tickets"][number];
export type TicketDetailData = Awaited<ReturnType<typeof fetchTicket>>;
export type TicketComment = TicketDetailData["comments"][number];
export type StaffTicketList = Awaited<ReturnType<typeof fetchStaffTickets>>;
export type StaffTicketSummary = StaffTicketList["tickets"][number];
export type StaffTicketDetailData = Awaited<ReturnType<typeof fetchStaffTicket>>;
export type SupportStats = Awaited<ReturnType<typeof fetchStats>>;
export type StaffPerson = Awaited<ReturnType<typeof fetchStaff>>["staff"][number];

export const supportQueries = {
  /** The signed-in person's workspace Tickets. */
  tickets: (client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "tickets"],
      queryFn: () => fetchTickets(client),
    }),

  ticket: (id: string, client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "tickets", id],
      queryFn: () => fetchTicket(client, id),
    }),

  /** The Staff queue, one page at a time. */
  staffTickets: (filters: StaffTicketFilters, client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "queue", filters],
      queryFn: () => fetchStaffTickets(client, filters),
      placeholderData: (previous) => previous,
    }),

  staffTicket: (id: string, client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "queue", "ticket", id],
      queryFn: () => fetchStaffTicket(client, id),
    }),

  stats: (client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "stats"],
      queryFn: () => fetchStats(client),
    }),

  /** Staff who can be assigned a Ticket. Changes rarely. */
  staff: (client: SupportClient = api) =>
    queryOptions({
      queryKey: ["support", "people"],
      queryFn: () => fetchStaff(client),
      staleTime: 5 * 60_000,
    }),
};
